<template>
  <base-drawer :show="show" :title="drawerTitle" width="520px" @close="onClose">
    <template v-if="drawerView === 'detail'" #header-extra>
      <div v-if="!isNew && categoryName" class="resource-drawer__meta">
        <badge
          type="default"
          class="resource-drawer__category"
          :style="categoryBadgeStyle"
        >
          {{ categoryName }}
        </badge>
      </div>
    </template>

    <detail-view
      v-if="drawerView === 'detail'"
      :slot-data="slot"
      :errs="errs"
      :is-new="isNew"
      :can-edit="hasPermissions('resource_slots_update')"
      :can-delete="hasPermissions('resource_slots_delete')"
      :saving="saving"
      :company-group-id="companyGroupId"
      :games="games"
      :block-statuses="blockStatuses"
      :priorities="priorities"
      :date-format="dateFormat"
      :time-format="timeFormat"
      :first-day-of-week="firstDayOfWeek"
      :fetch-staff-suggestions="querySearchStaff"
      :fetch-booking-suggestions="querySearchBookings"
      @toggle-lock="toggleAssignmentLock"
      @remove-assignment="removeBookingAssignment"
      @view-activity-log="onViewActivityLog"
      @delete="onDelete"
    />

    <activity-log-view
      v-else-if="drawerView === 'activity-log'"
      :entries="activityLog"
      :loading="activityLogLoading"
      :date-format="dateFormat"
      :time-format="timeFormat"
      @back="drawerView = 'detail'"
    />

    <template v-if="drawerView === 'detail'" #footer>
      <div class="resource-drawer__footer">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="!hasPermissions('resource_slots_update') || saving"
          @click="onSave"
        >
          <i v-if="saving" class="el-icon-loading mr-1"></i>
          {{ isNew ? 'Create resource slot' : 'Save changes' }}
        </base-button>
      </div>
    </template>

    <base-alert-dialog
      :show="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :variant="confirmDialog.variant"
      show-cancel
      :confirm-text="confirmDialog.confirmText"
      :cancel-text="confirmDialog.cancelText"
      @confirm="answerConfirmation(true)"
      @cancel="answerConfirmation(false)"
    />
  </base-drawer>
</template>

<script>
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { API_LOCATION } from '@/api-config';
import { Badge } from '@/components';
import moment from 'moment';
import swal from 'sweetalert2';
import { AUTO_UNBLOCK_WARNING } from '@/util/autoUnblockWarning';
import { parseTime as parseTimeFlexible } from '@/plugins/dates';
import DetailView from './drawer-views/DetailView.vue';
import ActivityLogView from './drawer-views/ActivityLogView.vue';

export default {
  name: 'ResourceDrawer',

  components: {
    DetailView,
    ActivityLogView,
    Badge
  },

  props: {
    show: { type: Boolean, default: false },
    resourceSlot: { type: Object, default: null },
    companyId: { type: [Number, String], required: true },
    companyGroupId: { type: [Number, String], required: true },
    games: { type: Array, default: () => [] },
    blockStatuses: { type: Array, default: () => [] },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    timeFormat: { type: String, default: 'hh:mm A' },
    firstDayOfWeek: { type: Number, default: 0 },
    initialDate: { type: [String, Date], default: null },
    initialStartTime: { type: String, default: null },
    initialEndTime: { type: String, default: null },
    priorities: { type: Array, default: () => [1, 2, 3, 4, 5] },
    validateDelete: { type: Function, default: null },
    // 'detail' | 'activity-log' — lets the parent open the drawer directly into the log subview.
    initialView: {
      type: String,
      default: 'detail',
      validator: v => ['detail', 'activity-log'].includes(v)
    }
  },

  data() {
    return {
      drawerView: 'detail',
      saving: false,
      slot: {},
      errs: {
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        resource_category_id: ''
      },
      activityLog: [],
      activityLogLoading: false,
      // Auto-unblock state when the drawer opened; the save warns only when it turns on.
      autoUnblockWasOn: false,
      confirmDialog: { show: false, title: '', message: '', variant: 'warning', confirmText: 'Confirm', cancelText: 'Cancel', resolve: null }
    };
  },

  computed: {
    isNew() {
      return !this.resourceSlot || !this.resourceSlot.id;
    },

    resourceTitle() {
      if (this.isNew) return 'New resource slot';
      if (this.slot.first_name && this.slot.last_name) {
        return `${this.slot.first_name} ${this.slot.last_name}`.trim();
      }
      return this.slot.label || 'Resource slot';
    },

    drawerTitle() {
      if (this.drawerView === 'activity-log') return this.resourceTitle;
      return this.resourceTitle;
    },

    categoryName() {
      return this.slot.category_name || '';
    },

    categoryColor() {
      return this.slot.category_color || null;
    },

    categoryBadgeStyle() {
      if (!this.categoryColor) return {};
      return {
        backgroundColor: this.hexToRgba(this.categoryColor, 0.18),
        borderColor: this.hexToRgba(this.categoryColor, 0.45),
        color: this.categoryColor
      };
    },

    formattedSlotDate() {
      if (!this.slot.start_date) return '';
      const start = moment(this.slot.start_date).format(this.dateFormat);
      const end = this.slot.end_date ? moment(this.slot.end_date).format(this.dateFormat) : '';
      if (end && end !== start) return `${start} - ${end}`;
      return start;
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.activityLog = [];
        if (this.isNew) {
          this.drawerView = 'detail';
          this.initCreateSlot();
        } else {
          this.initEditSlot();
          // If parent requested the activity-log subview, jump straight in.
          if (this.initialView === 'activity-log') {
            this.drawerView = 'activity-log';
            this.fetchActivityLog();
          } else {
            this.drawerView = 'detail';
          }
        }
      }
    },
    // Re-normalize if parent swaps the slot while the drawer stays open.
    // Only fires on mid-open swaps (both oldId and newId defined) — initial
    // open is handled by the `show` watcher above, which also respects initialView.
    'resourceSlot.id'(newId, oldId) {
      if (oldId !== undefined && newId !== undefined && this.show && !this.isNew) {
        this.drawerView = 'detail';
        this.activityLog = [];
        this.initEditSlot();
      }
    }
  },

  methods: {
    hexToRgba(hex, alpha = 1) {
      if (!hex) return '';
      const clean = hex.replace('#', '');
      const full = clean.length === 3
        ? clean.split('').map(c => c + c).join('')
        : clean;
      const r = parseInt(full.substring(0, 2), 16);
      const g = parseInt(full.substring(2, 4), 16);
      const b = parseInt(full.substring(4, 6), 16);
      if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    parseTime(value) {
      return parseTimeFlexible(value);
    },

    toLocalDate(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      const dateStr = typeof value === 'string' && value.includes('T')
        ? value.split('T')[0]
        : value;
      const [year, month, day] = String(dateStr).split('-').map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    },

    parseLocalDate(dateStr) {
      if (!dateStr) return new Date();
      if (dateStr instanceof Date) return dateStr;
      return moment(dateStr, 'YYYY-MM-DD').toDate();
    },

    initCreateSlot() {
      const defaultStart = new Date();
      defaultStart.setHours(9, 0, 0, 0);
      const defaultEnd = new Date();
      defaultEnd.setHours(17, 0, 0, 0);

      this.slot = {
        start_date: this.initialDate ? this.parseLocalDate(this.initialDate) : new Date(),
        end_date: this.initialDate ? this.parseLocalDate(this.initialDate) : new Date(),
        start_time: this.initialStartTime ? this.parseTime(this.initialStartTime) : defaultStart,
        end_time: this.initialEndTime ? this.parseTime(this.initialEndTime) : defaultEnd,
        enable_autoblock: true,
        enable_auto_unblock: false,
        exclude_call_to_book_from_auto_unblock: false,
        saturate_on_first_assignment: false,
        include_start_end_time_in_blocking: false,
        apply_to_all_events: true,
        block_status: { id: 1, name: 'Blocked', label: 'blocked' },
        linked_games: [],
        resource_category_id: null,
        priority: 1,
        supported_event_count: 1,
        company_user_id: null,
        first_name: '',
        last_name: '',
        label: '',
        anonymous_resource: true,
        resource_assignee_value: '',
        resource_assignee_object: null,
        resource_assigned_value: '',
        resource_assigned_object: null,
        assignment: []
      };
      this.autoUnblockWasOn = false;
      this.resetErrors();
    },

    initEditSlot() {
      this.slot = this.normalizeSlot(this.resourceSlot);
      this.autoUnblockWasOn = Boolean(this.slot.enable_auto_unblock);
      this.resetErrors();
    },

    // Normalizes any resource-slot shape (raw API row, a view's originalData,
    // or the pre-processed row from ResourceCalendarView.resourceSlots) into the form
    // DetailView binds against.
    normalizeSlot(raw) {
      if (!raw) return {};
      const source = raw.originalData ? raw.originalData : raw;
      const parsed = JSON.parse(JSON.stringify(source));

      parsed.start_date = this.toLocalDate(parsed.start_date);
      parsed.end_date = this.toLocalDate(parsed.end_date);
      parsed.start_time = this.parseTime(parsed.start_time);
      parsed.end_time = this.parseTime(parsed.end_time);

      if (typeof parsed.assignment === 'string') {
        try { parsed.assignment = JSON.parse(parsed.assignment); } catch (e) { parsed.assignment = []; }
      }
      if (!Array.isArray(parsed.assignment)) parsed.assignment = [];
      if (typeof parsed.linked_games === 'string') {
        try { parsed.linked_games = JSON.parse(parsed.linked_games); } catch (e) { parsed.linked_games = []; }
      }
      if (!Array.isArray(parsed.linked_games)) parsed.linked_games = [];

      if (typeof parsed.block_status === 'string') {
        const match = (this.blockStatuses || []).find(s => s.label === parsed.block_status);
        parsed.block_status = match || { id: 1, name: 'Blocked', label: 'blocked' };
      } else if (!parsed.block_status || typeof parsed.block_status !== 'object') {
        parsed.block_status = { id: 1, name: 'Blocked', label: 'blocked' };
      }

      parsed.enable_autoblock = Boolean(parsed.enable_autoblock);
      parsed.enable_auto_unblock = Boolean(parsed.enable_auto_unblock);
      parsed.exclude_call_to_book_from_auto_unblock = Boolean(parsed.exclude_call_to_book_from_auto_unblock);
      parsed.saturate_on_first_assignment = Boolean(parsed.saturate_on_first_assignment);
      parsed.include_start_end_time_in_blocking = Boolean(parsed.include_start_end_time_in_blocking);
      if (parsed.apply_to_all_events === undefined || parsed.apply_to_all_events === null) {
        parsed.apply_to_all_events = true;
      } else {
        parsed.apply_to_all_events = Boolean(parsed.apply_to_all_events);
      }
      if (parsed.supported_event_count == null) parsed.supported_event_count = 1;
      if (!parsed.priority) parsed.priority = 1;

      parsed.anonymous_resource = !parsed.company_user_id;
      parsed.resource_assignee_value = '';
      parsed.resource_assignee_object = null;
      parsed.resource_assigned_value = '';
      parsed.resource_assigned_object = null;

      return parsed;
    },

    resetErrors() {
      this.errs = {
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        resource_category_id: ''
      };
    },

    onClose() {
      this.$emit('update:show', false);
      this.$emit('close');
    },

    hasPermissions(permission) {
      const p = JSON.parse(localStorage.getItem('permissions')) || [];
      return p.includes(permission);
    },

    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    validateSlot() {
      let valid = true;
      this.resetErrors();

      if (!this.slot.start_date) { this.errs.start_date = 'Start date is required'; valid = false; }
      if (!this.slot.end_date) { this.errs.end_date = 'End date is required'; valid = false; }
      if (!this.slot.start_time) { this.errs.start_time = 'Start time is required'; valid = false; }
      if (!this.slot.end_time) { this.errs.end_time = 'End time is required'; valid = false; }

      if (this.slot.start_time && this.slot.end_time && this.slot.start_date && this.slot.end_date) {
        const startDate = moment(this.slot.start_date).format('YYYY-MM-DD');
        const endDate = moment(this.slot.end_date).format('YYYY-MM-DD');
        const startTime = moment(this.slot.start_time).format('HH:mm:ss');
        const endTime = moment(this.slot.end_time).format('HH:mm:ss');
        const startDT = moment(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm:ss');
        const endDT = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm:ss');
        if (endDT.isSameOrBefore(startDT)) {
          this.errs.end_time = 'End date/time must be after start date/time';
          valid = false;
        }
      }

      return valid;
    },

    async onSave() {
      if (!this.validateSlot()) return;

      if (this.slot.enable_autoblock && !this.slot.apply_to_all_events && (!this.slot.linked_games || this.slot.linked_games.length <= 0)) {
        const ok = await this.askConfirmation({
          title: 'Are you sure?',
          message: 'No events will be blocked because none are associated with this slot. Save anyway?',
          confirmText: 'I am sure'
        });
        if (!ok) return;
      }

      if (this.slot.enable_auto_unblock && !this.autoUnblockWasOn) {
        const ok = await this.askConfirmation(AUTO_UNBLOCK_WARNING);
        if (!ok) return;
      }

      this.saving = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const defaultBlockStatus = { id: 1, name: 'Blocked', label: 'blocked' };

        const data = {
          start_time: moment(this.slot.start_time).format('HH:mm:ss'),
          end_time: moment(this.slot.end_time).format('HH:mm:ss'),
          count: this.slot.count || 1,
          start_date: moment(this.slot.start_date).format('YYYY-MM-DD'),
          end_date: moment(this.slot.end_date).format('YYYY-MM-DD'),
          enable_autoblock: this.slot.enable_autoblock,
          enable_auto_unblock: this.slot.enable_auto_unblock,
          exclude_call_to_book_from_auto_unblock: this.slot.exclude_call_to_book_from_auto_unblock,
          block_status: this.slot.block_status || defaultBlockStatus,
          label: this.slot.label || 'Anonymous',
          supported_event_count: this.slot.supported_event_count,
          company_user_id: this.slot.company_user_id,
          assignment: this.slot.assignment,
          linked_games: this.slot.linked_games,
          resource_category_id: this.slot.resource_category_id,
          include_start_end_time_in_blocking: this.slot.include_start_end_time_in_blocking,
          apply_to_all_events: this.slot.apply_to_all_events,
          priority: this.slot.priority,
          saturate_on_first_assignment: this.slot.saturate_on_first_assignment
        };

        if (this.isNew) {
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/staff`;
          await axios.post(url, data, config);
          this.$notify({ type: 'success', message: 'Resource slot created. Availability updates in the background and the calendar refreshes when it finishes.', timeout: 8000 });
        } else {
          data.schedule_id = this.slot.schedule_id;
          data.today = moment().format('YYYY-MM-DD');
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/staff/${this.slot.id}`;
          await axios.put(url, data, config);
          this.$notify({ type: 'success', message: 'Resource slot saved. Availability updates in the background and the calendar refreshes when it finishes.', timeout: 8000 });
        }

        this.$emit('resource-changed');
        this.onClose();
      } catch (error) {
        console.error('Error saving resource slot:', error);
        if (error.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.$notify({ type: 'danger', message: error.response?.data || `Failed to ${this.isNew ? 'create' : 'update'} resource slot`, timeout: 8000 });
        }
      } finally {
        this.saving = false;
      }
    },

    // Promise form of the confirm dialog: resolves true on confirm, false on cancel or dismiss.
    askConfirmation({ title, message, variant = 'warning', confirmText = 'Confirm', cancelText = 'Cancel' }) {
      return new Promise(resolve => {
        this.confirmDialog = { show: true, title, message, variant, confirmText, cancelText, resolve };
      });
    },

    answerConfirmation(answer) {
      const resolve = this.confirmDialog.resolve;
      this.confirmDialog = { ...this.confirmDialog, show: false, resolve: null };
      if (resolve) resolve(answer);
    },

    async onDelete() {
      let preflightMessage = 'This resource slot will be permanently deleted.';
      if (typeof this.validateDelete === 'function') {
        try {
          const result = await this.validateDelete(this.slot);
          if (result && result !== 'Success') {
            preflightMessage = result;
          }
        } catch (e) {
          console.error('Pre-delete validation failed:', e);
        }
      }

      const result = await swal({
        title: 'Are you sure?',
        text: preflightMessage,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#fd5d93'
      });
      if (!result.value) return;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/staff/${this.slot.id}`;
        await axios.delete(url, config);
        this.$notify({ type: 'success', message: 'Resource slot deleted', timeout: 2000 });
        this.$emit('resource-changed');
        this.onClose();
      } catch (error) {
        console.error('Error deleting resource slot:', error);
        if (error.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          swal('Error', error.response?.data || 'Failed to delete resource slot', 'error');
        }
      }
    },

    async fetchActivityLog() {
      if (this.isNew || this.activityLogLoading) return;
      this.activityLogLoading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/staff/${this.slot.id}/log`;
        const response = await axios.get(url, config);
        this.activityLog = response.data || [];
      } catch (error) {
        console.error('Error fetching activity log:', error);
        this.activityLog = [];
      } finally {
        this.activityLogLoading = false;
      }
    },

    onViewActivityLog() {
      this.drawerView = 'activity-log';
      if (this.activityLog.length === 0) this.fetchActivityLog();
    },

    async toggleAssignmentLock(item, index) {
      // Unsaved (create) slots have no id — flip locally only.
      if (!this.slot.id) {
        this.$set(this.slot.assignment, index, {
          ...this.slot.assignment[index],
          is_locked: item.is_locked ? 0 : 1
        });
        return;
      }
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/staff/${this.slot.id}/assign`;
        const response = await axios.put(url, { booking_slot_id: item.booking_slot_id }, config);
        const newLockState = response.data && typeof response.data.is_locked !== 'undefined'
          ? response.data.is_locked
          : (item.is_locked ? 0 : 1);
        this.$set(this.slot.assignment, index, {
          ...this.slot.assignment[index],
          is_locked: newLockState
        });
        this.$notify({
          type: 'success',
          message: newLockState ? 'Assignment locked' : 'Assignment unlocked',
          timeout: 2000
        });
        this.$emit('assignment-updated');
      } catch (error) {
        console.error('Failed to toggle assignment lock:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to update assignment lock',
          timeout: 3000
        });
      }
    },

    removeBookingAssignment(item, index) {
      this.slot.assignment.splice(index, 1);
    },

    async querySearchStaff(queryString, cb) {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/company-users?count=10000000&autocomplete=${encodeURIComponent(queryString)}`;
        const response = await axios.get(url, config);
        const results = response.data.company_users.map(user => ({
          value: `${user.first_name} ${user.last_name}`,
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email
        }));
        cb(results);
      } catch (error) {
        console.error('Error searching staff:', error);
        cb([]);
      }
    },

    async querySearchBookings(queryString, cb) {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const startDate = moment(this.slot.start_date).format('YYYY-MM-DD');
        const startTime = moment(this.slot.start_time).format('HH:mm');
        const endDate = moment(this.slot.end_date).format('YYYY-MM-DD');
        const endTime = moment(this.slot.end_time).format('HH:mm');

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}` +
          `/bookings/assignable?booking=${encodeURIComponent(queryString)}` +
          `&start_date=${startDate}&start_time=${startTime}` +
          `&end_date=${endDate}&end_time=${endTime}`;

        const response = await axios.get(url, config);
        const results = response.data.bookings.map(booking => ({
          value: JSON.stringify(booking),
          id: booking.id,
          name: booking.name,
          booking_date: booking.booking_date,
          end_date: booking.end_date,
          end_time: booking.end_time,
          start_time: booking.start_time,
          status: booking.status
        }));
        cb(results);
      } catch (error) {
        console.error('Error searching bookings:', error);
        cb([]);
      }
    },

    onKeydown(e) {
      if (e.key === 'Escape' && this.show) {
        this.onClose();
      }
    }
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  }
};
</script>

<style lang="scss" scoped>
.resource-drawer__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  margin-bottom: -8px;
  flex-wrap: wrap;
}

.resource-drawer__category {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border: 1px solid transparent;
}

.resource-drawer__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>

</style>
